import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import React from "react";
import { LocStoreInstance } from ".";
import {
  PostMessageCVPFocusTextId,
  PostMessageTypes,
} from "../constants/PostMessageTypes";
import { lp } from "../utils/logPrefix";
import { loc } from "./loc";
import { split, SplitReplacement } from "./split";
import { textPreviewMode } from "./textPreviewMode";

const isCtrlDown = observable.box(false);

document.addEventListener("keydown", (e) => {
  if (e.ctrlKey) {
    isCtrlDown.set(true);
  }
});

document.addEventListener("keyup", (e) => {
  if (!e.ctrlKey) {
    isCtrlDown.set(false);
  }
});

/**
 * Call as a function instead of using jsx for toString() support.
 * (Will return text instead of [object Object] when component is used in text fields etc)
 */
export function cvpTextPreview(props: LocalizeTextProps) {
  const x = <CvpTextPreview {...props} />;
  return {
    ...x,
    toString: _toString.bind(props),
    _wsHelpTextWithToString: true,
    _isHelpText: true,
  };
}

const CvpTextPreview = observer((props: LocalizeTextProps) => {
  const hasOverride = !!props.locStoreInstance.overrides?.items.find((item) => {
    return (
      item.idText === props.id &&
      item.systemText === props.text &&
      item.locale === props.locStoreInstance.gotTranslationsForLocale
    );
  });

  const text = localizedText(props);

  return (
    <span
      style={{
        background: hasOverride ? "hotpink" : "pink",
        cursor: isCtrlDown.get() ? "pointer" : "inherit",
      }}
      onClick={(e) => {
        if (!e.ctrlKey) {
          return;
        }

        e.preventDefault();
        e.persist();
        e.stopPropagation();

        const msg: PostMessageCVPFocusTextId = {
          type: PostMessageTypes.CVPFocusTextId,
          textId: props.id,
        };

        console.log(lp("window.parent.postMessage"), msg);
        window.parent.postMessage(msg, origin);
      }}
    >
      {text}
      {hasOverride && !text && "[EMPTY_STRING]"}
    </span>
  );
});

function localizedText(props: LocalizeTextProps) {
  let s = props.text;

  if (!s) {
    s = "[EMPTY_STRING]";
  }

  if (s && props.locType === "loc") {
    return loc(
      s,
      props.searchWords,
      props.stringContext,
      props.id,
      props.locStoreInstance
    );
  } else if (s && props.locType === "split") {
    return split(
      s,
      props.replacements,
      props.searchWords,
      props.stringContext,
      props.id,
      props.locStoreInstance
    );
  }

  return "[EMPTY_STRING]";
}

function _toString() {
  const result = localizeText(this);

  if (result === null || result === undefined) return result;
  if (typeof result === "object" && typeof result.toString === "function") {
    return result.toString();
  }

  return result;
}

interface LocalizeTextProps {
  id: string;
  text: string;
  searchWords?: string[];
  locType: "pass" | "loc" | "split";
  replacements?: SplitReplacement;
  stringContext?: string;
  className?: string;
  locStoreInstance: LocStoreInstance;
}

const localizeText = (props: LocalizeTextProps) => {
  if (!props.text && textPreviewMode.get()) {
    props.text = "[EMPTY_STRING]";
  }

  if (props.text && props.locType === "loc") {
    return loc(
      props.text,
      null,
      props.stringContext,
      props.id,
      props.locStoreInstance
    );
  } else if (props.text && props.locType === "split") {
    return split(
      props.text,
      props.replacements,
      props.searchWords,
      props.stringContext,
      props.id,
      props.locStoreInstance
    );
  }

  return "[EMPTY_STRING]";
};
